// @ts-strict-ignore
/**
 * @module TelephonyConfig
 */
import { Subject, Subscription, filter } from 'rxjs';

import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { WebrtcGatewayService } from '../webrtcGateway/webrtcGateway.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { TelephonyRoutingDevice, TelephonyRoutingDeviceTypes, TelephonyRoutingErrors, TelephonyRoutingMode, TelephonyRoutingModeRB} from 'rainbow-web-sdk/models/telephonyConfig.model';
import { TelephonyCCDAgentService } from '../telephony/telephonyCcdAgent.service';
import { UserServiceEvents, UserServiceRB } from '../users/user.service';


/** @internal */
export const TELEPHONYCONFIG_SVC = 'TelephonyConfigService';

/**
 * @eventProperty
 *TelephonyCOnfig service events send to other services
 * Can listen to it via `TelephonyConfigService.subscribe()` API
 */
export enum TelephonyConfigServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when the telephony routing device list has changed.
     *
     * **Event Data**
     * - **RoutingDeviceList**: The list of all devices (as TelephonyRoutingDevice[]) present for the user
     */
    ON_ROUTING_LIST_CHANGE = "ON_ROUTING_LIST_CHANGE",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the telephony routing mode has changed
     *
     * **Event Data**
     * - **routingMode**: The routingMode (as TelephonyRoutingMode) return the telephony Config State
     */
    ON_ROUTING_MODE_CHANGE = "ON_ROUTING_MODE_CHANGE"
}

interface HybridRoutingData {
    featureActivated: boolean;
    modeActivated: boolean;
    destination: string;
    makeCallInitiatorIsMain: boolean;
    otherPhoneModeDenied: boolean;
    nomadicModeInitialized: boolean;
}

interface SipWiseRoutingData {
    devicesList: any[];
    ringingDestinations: [];
    alsoOfficePhoneRingEnabled: true;
}


export interface TelephonyConfigService {

    /**
     * Subscribe to the telephonyConfig updates, all events are of RBEvent<TelephonyConfigServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<TelephonyConfigServiceEvents>) => any, eventNames?: TelephonyConfigServiceEvents | TelephonyConfigServiceEvents[]): Subscription;

    /**
     * Get the routing devices list
     */
    getRoutingDevicesList(): TelephonyRoutingDevice[];

    /**
     * Set the telephony routing mode
     * @param selectedMode -  the device which is selected like Telephony Config Device
     * @param destination - if Telephony Config Device is Other_Phone then the destination must be entered with the new phone number
     * A ON_ROUTING_MODE_CHANGE event is sent afterwards at the completion of the operation to notify about the new telephony Config status.
     */
    setRoutingDeviceMode(selectedMode: TelephonyRoutingDeviceTypes, destination?: string);

    /**
     * Set the Office Phone Ringing Option
     * @param isOnlyOfficePhoneRinging  - In the case of office phone is selected and the user has mobile phones (work or perso) associated, if TRUE only office phone rings else FALSE the office phone and the mobile phone (mobile pro or mobile perso) ring
     */
    setOfficePhoneRingingOption(isOnlyOfficePhoneRinging: boolean);

    /**
     * Get the routing mode
     * A ON_ROUTING_MODE_CHANGE event is sent afterwards at the completion of the operation to notify about the new telephony Config status.
     * @returns Returns TelephonyRoutingMode
     */
    getRoutingMode(): TelephonyRoutingMode;

}

/**
 * @internal
 */
export class TelephonyConfigServiceRB extends Service implements TelephonyConfigService {


    // Observables for events sent by TelephonyConfigService
    private rxSubject: Subject<any> = new Subject<any>();
    private eventUnregisters: any[] = [];
    private contact: Contact = null;
    private isSipWise: boolean = false;
    private webRTCDevice: any = null;
    private isTelephonyAllowed: boolean = false;
    private isNomadicFeatureAllowed: boolean = false;
    private isNomadicFeatureActivated: boolean = false;
    private is3dPartyFeatureActivated: boolean = false;
    private areTelephonySettingsEnabled: boolean = false;
    private mediaPillarNumber: string = "";
    private isNomadicModeActivated: boolean = false;
    private isMakeCallInitiatorIsMain: boolean = false;
    public isOtherPhoneModeDenied: boolean = false;
    public isCCDAgentEnabled: boolean = false;
    private isMediaPillarConfigured: boolean = false;
    private routingDeviceList: TelephonyRoutingDevice[] = [];
    private routingMode: TelephonyRoutingMode;
    private oldRoutingMode: TelephonyRoutingMode;
    //private newRoutingMode: RoutingMode;
    private sipDevice: any = null;
    private nomadicPhone: string = "";
    private nomadicToNumber: string = "";
    private isMediaPillarAvailable: boolean = false;
    private isTelephonyStarted: boolean = false;
    private subscriptions: Subscription[] = [];

    // Services
    private logger: LoggerService = LoggerService.getInstance();
    private telephonyService: TelephonyService = TelephonyService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private webrtcGatewayService: WebrtcGatewayService = WebrtcGatewayService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private ccdAgentService: TelephonyCCDAgentService = TelephonyCCDAgentService.getInstance();
    private userService: UserServiceRB = UserServiceRB.getInstance();

    /**
     * Returns the TelephonyConfig service singleton
     */
    public static getInstance(): TelephonyConfigServiceRB {
        return Service.create(TELEPHONYCONFIG_SVC, TelephonyConfigServiceRB);
    }

    public static build(): TelephonyConfigServiceRB {
        return new TelephonyConfigServiceRB();
    }

    private constructor() {
        super();
    }

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------

    public async start() {
        try {
            this.logger.info('[TelephonyConfigService] === STARTING ===');
            this.routingMode = new TelephonyRoutingModeRB();
            this.oldRoutingMode = new TelephonyRoutingModeRB();
            //telephony Stuff
            //this.storeCurrentRoutingMode(this.currentRoutingMode, { currentMode: "dummyInit" });//dummy init to force the 1st pullTelephonyMode()
            this.computeTelephonySituation();
            this.logger.info(`[TelephonyConfigService] === STARTED ===`);
        }
        catch (error) {
            /**/
        }
    }

    public async reconnect() {
        try {
            this.logger.info("[TelephonyConfigService] reconnect");
            if (!this.routingMode) { this.routingMode = new TelephonyRoutingModeRB(); }
            if (!this.oldRoutingMode) { this.oldRoutingMode = new TelephonyRoutingModeRB(); }

            this.computeTelephonySituation();
        }
        catch (error) { this.logger.error(`[TelephonyConfigService] reconnect -- failure -- ${error.message}`); }
    }

    public async stop() {
        this.logger.info('[TelephonyConfigService] === STOPPING ===');
        this.clearSubscriptions();
        this.logger.info('=== STOPPED ===');
    }


    private clearSubscriptions() {
        if (this.eventUnregisters && this.eventUnregisters.length) {
            this.eventUnregisters.forEach((listener) => {
                listener();
            });
            this.eventUnregisters = [];
        }
        this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        this.subscriptions = [];
    }

    /**
     * Subscribe to the nomadic updates, all events are of RBEvent<TelephonyConfigServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<TelephonyConfigServiceEvents>) => any, eventNames?: TelephonyConfigServiceEvents | TelephonyConfigServiceEvents[]): Subscription {
        const observable = eventNames ? this.rxSubject.pipe(filter((event: RBEvent<TelephonyConfigServiceEvents>) => {
            if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
            return event.name === eventNames;
        })) : this.rxSubject;

        return observable.subscribe((event: RBEvent<TelephonyConfigServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[TelephonyConfigService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * @public
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     * TelephonyConfigService events send to other services via sendEvent()
     * Can listen to it via TelephonyConfigServiceRB.subscribe() API
     */
    private sendEvent(name: string, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    // ------------------------------------------------------------
    //          EVENT LISTENERS ( Handling of events)
    // ------------------------------------------------------------

    private handleNomadicState = (__event: any, nomadicObject: HybridRoutingData) => {
        const newRoutingDeviceList: TelephonyRoutingDevice[] = [];
        this.isNomadicFeatureActivated = nomadicObject.featureActivated;
        if (this.isNomadicFeatureActivated) {
            this.mediaPillarNumber = this.webrtcGatewayService.getMyMediaPillarRemoteExtension();
            this.isNomadicModeActivated = nomadicObject.modeActivated;
            this.isMakeCallInitiatorIsMain = nomadicObject.makeCallInitiatorIsMain;
            this.isOtherPhoneModeDenied = nomadicObject.otherPhoneModeDenied;
            if (!this.isMakeCallInitiatorIsMain) {
                const destination = nomadicObject.destination;
                if (this.contact.phoneProCan === destination && destination !== "") { this.nomadicPhone = "phonePro"; }//RQRAINB-2660++
                else if (this.contact.mobileProCan === destination && destination !== "") { this.nomadicPhone = "mobilePro"; }
                else if (this.contact.phonePersoCan === destination && destination !== "") { this.nomadicPhone = "phonePerso"; }
                else if (this.contact.mobilePersoCan === destination && destination !== "") { this.nomadicPhone = "mobilePerso"; }//RQRAINB-2660--
                else if (this.mediaPillarNumber === destination) {
                    this.nomadicPhone = "mediaPillar";
                    this.webrtcGatewayService.mediaPillarUserSelectAndPolling(true);
                }
                else { this.nomadicPhone = "callNomadicToNumber"; }
                this.nomadicToNumber = destination;
            }
            else { this.nomadicPhone = "phonePro"; }
        }
        if (this.isMakeCallInitiatorIsMain && (this.contact.mobilePro || this.contact.mobilePerso)) {
            if (this.isNomadicModeActivated) {
                if (this.settingsService.getSetting('OnlyOfficePhoneRing') !== 'false') {
                    this.settingsService.setSetting('OnlyOfficePhoneRing', 'false');
                    this.logger.info('[TelephonyConfigService] [NOMADIC] set in localstorage OnlyOfficePhoneRing : false');
                }
            }
            else {
                if (this.settingsService.getSetting('OnlyOfficePhoneRing') !== 'true') {
                    this.settingsService.setSetting('OnlyOfficePhoneRing', 'true');
                    this.logger.info('[TelephonyConfigService] [NOMADIC] set in localstorage OnlyOfficePhoneRing : true');
                }
            }
        }
        if (!this.isSipWise && !this.isCCDAgentEnabled) {
            if (this.isMediaPillarConfigured && this.contact.useComputerMode && this.isNomadicFeatureAllowed && this.isNomadicFeatureActivated) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.COMPUTER, deviceAvailable: this.isMediaPillarAvailable});
            }
            if (!this.contact.isVirtualTerm) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.OFFICE_PHONE, deviceAvailable: true});
            }
            if (this.contact.mobilePro && this.contact.useOtherPhoneMode && this.isNomadicFeatureAllowed && this.isNomadicFeatureActivated) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.MOBILE_PRO, deviceAvailable: true});
            }
            if (this.contact.mobilePerso && this.contact.useOtherPhoneMode && this.isNomadicFeatureAllowed && this.isNomadicFeatureActivated) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.MOBILE_PERSO, deviceAvailable: true});
            }
            if (this.contact.phonePerso && this.contact.useOtherPhoneMode && this.isNomadicFeatureAllowed && this.isNomadicFeatureActivated) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.PHONE_PERSO, deviceAvailable: true});
            }
            if (!this.isOtherPhoneModeDenied && !this.isCCDAgentEnabled && this.contact.useOtherPhoneMode && this.isNomadicFeatureAllowed && this.isNomadicFeatureActivated) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.OTHER_PHONE, deviceAvailable: true});
            }
        }
        if (this.isSipWise) {
            if (this.webRTCDevice) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.COMPUTER, deviceAvailable: this.webRTCDevice.state !== 'outOfService'});
            }
            if (this.sipDevice) {
                newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.OFFICE_PHONE, deviceAvailable: this.sipDevice.state !== 'outOfService'});
            }
        }

        this.broadcastRoutingDevicesList(newRoutingDeviceList);
        this.computeMode();
    };


    private evalChangeModePossible() {
        this.routingMode.isTelephonyRoutingModeCanBeChanged = (this.routingMode.isTelephonyStarted !== false) && !((this.contact.telStatus === "EVT_SERVICE_INITIATED") || (this.contact.telStatus === "EVT_ESTABLISHED"));
    }

    private handleCallSipWiseState(__event: any, currentDeviceType: string, routingObject: SipWiseRoutingData) {
        const newRoutingDeviceList: TelephonyRoutingDevice[] = [];
        if (!currentDeviceType) {
            this.logger.debug('[TelephonyConfigService] handleCallSipWiseState : anomaly currentDeviceType');
        }
        this.webRTCDevice = routingObject.devicesList.find((device) => {
            return device.type === "webrtc";
        });
        this.sipDevice = routingObject.devicesList.find((device) => {
            return device.type === "sip";
        });
        if (this.webRTCDevice) {
            newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.COMPUTER, deviceAvailable: this.webRTCDevice.state !== 'outOfService'});
        }
        if (this.sipDevice) {
            newRoutingDeviceList.push({deviceType: TelephonyRoutingDeviceTypes.OFFICE_PHONE, deviceAvailable: this.sipDevice.state !== 'outOfService'});
        }
        this.broadcastRoutingDevicesList(newRoutingDeviceList);
        this.computeMode();
    }

    // ------------------------------------------------------------
    //                      PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    public async setRoutingDeviceMode(deviceTypeSelected: TelephonyRoutingDeviceTypes, destination: string = "") {
        try {
            if (this.routingMode.isTelephonyRoutingModeCanBeChanged) {
                if (!this.isSipWise) {
                    switch (deviceTypeSelected) {
                        case TelephonyRoutingDeviceTypes.COMPUTER: this.telephonyService.nomadicLogin(this.mediaPillarNumber, true); break;
                        case TelephonyRoutingDeviceTypes.MOBILE_PRO: this.telephonyService.nomadicLogin(this.contact.mobilePro); break;
                        case TelephonyRoutingDeviceTypes.PHONE_PERSO: this.telephonyService.nomadicLogin(this.contact.phonePerso); break;
                        case TelephonyRoutingDeviceTypes.MOBILE_PERSO: this.telephonyService.nomadicLogin(this.contact.mobilePerso); break;
                        case TelephonyRoutingDeviceTypes.OTHER_PHONE: { //RQRAINB-11813
                            if (destination) {
                                this.telephonyService.nomadicLogin(destination);
                            } else {
                                throw new Error("destination phone number is missing");
                            }
                            break;
                        }
                        case TelephonyRoutingDeviceTypes.OFFICE_PHONE: {
                            if (this.contact.mobilePro || this.contact.mobilePerso) {
                                const onlyOfficePhoneRing = (this.settingsService.getSetting('OnlyOfficePhoneRing') === "true");
                                this.logger.info('[TelephonyConfigService] [NOMADIC] get in localstorage OnlyOfficePhoneRing : ' + this.settingsService.getSetting('OnlyOfficePhoneRing'));
                                if (onlyOfficePhoneRing) {
                                    this.telephonyService.nomadicLogout();
                                } else {
                                    const defaultNumber = this.contact.mobilePro ? this.contact.mobilePro : this.contact.mobilePerso;
                                    await this.telephonyService.nomadicLogin(defaultNumber, false, true);
                                    await this.telephonyService.nomadicLoginOnOfficePhone();
                                }
                            }
                            else { this.telephonyService.nomadicLogout(); }
                            break;
                        }
                        default: break;
                    }
                }
                if (this.isSipWise) {
                    if (deviceTypeSelected === TelephonyRoutingDeviceTypes.COMPUTER && Boolean(this.isMediaPillarConfigured && this.contact.useComputerMode)) {
                        this.telephonyService.setSipWiseCurrentDevice(this.webRTCDevice.deviceId);
                    }
                    else if (deviceTypeSelected === TelephonyRoutingDeviceTypes.OFFICE_PHONE && Boolean(!this.contact.isVirtualTerm)) {
                        this.telephonyService.setSipWiseCurrentDevice(this.sipDevice.deviceId);
                    }
                    this.mediaPillarPollingManagement(deviceTypeSelected);
                }
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setRoutingMode');
            throw errorInfo.error;
        }
    }

    public async setOfficePhoneRingingOption(isOnlyOfficePhoneRinging: boolean) {
        try {
            this.logger.info('[TelephonyConfigService] [NOMADIC] setOfficePhoneRingingOption');
            if (this.routingMode.isTelephonyRoutingModeCanBeChanged && !this.isSipWise && this.routingMode.currentDeviceType === TelephonyRoutingDeviceTypes.OFFICE_PHONE) {
                if (this.contact.mobilePro || this.contact.mobilePerso) {
                    if (isOnlyOfficePhoneRinging) {
                        this.telephonyService.nomadicLogout();
                    } else {
                        const defaultNumber = this.contact.mobilePro ? this.contact.mobilePro : this.contact.mobilePerso;
                        await this.telephonyService.nomadicLogin(defaultNumber, false, true);
                        await this.telephonyService.nomadicLoginOnOfficePhone();
                    }
                }
                else { this.telephonyService.nomadicLogout(); }
            }
            else {
                throw new Error("Impossible to execute this request");//RQRAINB-11813
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setRoutingMode');
            throw errorInfo.error;
        }
    }

    public getRoutingMode() {
        return this.routingMode;
    }

    public getRoutingDevicesList() {
        return this.routingDeviceList;
    }

    // ------------------------------------------------------------
    //                      PRIVATE METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    /**
     * Compute telephony situation
     * @param phoneNumber - Phonenumber which will be monitored
     * @param isUnchangeableNumber - Allow to define the phonenumber like a rainbowNumber
     * @param NotTakeIntoAccount - The result won't be taken in account (above used un graphical interface)
     */
    private computeTelephonySituation() {
        this.clearSubscriptions();
        try { //RQRAINB-11813++ add tryCatch for SDK protection
        // Get contact
            this.contact = this.contactService.userContact;

            const telConfig = {
                pbxId: this.contact.pbxId,
                phoneInternalNumber: this.contact.phoneInternalNumber,
                hasPhoneProDdiNumber: this.contact.hasPhoneProDdiNumber,
                telephonySettingsEnabled: this.contact.telephonySettingsEnabled,
                isVirtualTerm: this.contact.isVirtualTerm,
                useComputerMode: this.contact.useComputerMode,
                useOtherPhoneMode: this.contact.useOtherPhoneMode,
                phoneProCan: this.contact.phoneProCan,
                mobileProCan: this.contact.mobileProCan,
                phonePersoCan: this.contact.phonePersoCan,
                mobilePersoCan: this.contact.mobilePersoCan,
                mobilePro: this.contact.mobilePro,
                mobilePerso: this.contact.mobilePerso,
                phonePerso: this.contact.phonePerso
            };
            if (this.contact.ccdAgent) {
                telConfig['ccdAgent'] = { mode: this.contact.ccdAgent.mode, agentPhoneNumber: this.contact.ccdAgent.agentPhoneNumber };
            }

            this.isSipWise = this.telephonyService.isSipWiseRoot();
            this.isTelephonyAllowed = this.contact.pbxId !== null && this.contact.pbxId !== "" && (this.contact.phoneInternalNumber !== null || this.contact.hasPhoneProDdiNumber);
            this.areTelephonySettingsEnabled = this.contact.telephonySettingsEnabled;
            this.routingMode.isTelephonyStarted = false;

            if (this.isTelephonyAllowed) {

                // Handle telephony state
                this.routingMode.isTelephonyStarted = this.telephonyService.started;
                this.eventUnregisters.push(this.eventService.subscribe('ON_TELEPHONY_STATUS_CHANGED_EVENT', () => {
                    this.routingMode.isTelephonyStarted = this.telephonyService.started;
                    this.mediaPillarNumber = this.webrtcGatewayService.getMyMediaPillarRemoteExtension();
                    this.handleNomadicState(null, this.telephonyService.nomadicObject);
                }));

                // Handle mediaPillar state
                this.isMediaPillarConfigured = this.webrtcGatewayService.isMediaPillarConfigured();
                this.isMediaPillarAvailable = this.webrtcGatewayService.isMediaPillarAvailable();
                this.mediaPillarNumber = this.webrtcGatewayService.getMyMediaPillarRemoteExtension();
                this.eventUnregisters.push(this.eventService.subscribe('ON_WEBRTCGATEWAY_STATE_CHG', (__event, mediaPillarAlive) => {
                    this.isMediaPillarAvailable = mediaPillarAlive;
                    this.isMediaPillarConfigured = this.webrtcGatewayService.isMediaPillarConfigured();
                    this.mediaPillarNumber = this.webrtcGatewayService.getMyMediaPillarRemoteExtension();
                    this.computeMode();
                }));

                // Handle nomadic state
                this.isNomadicFeatureAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_NOMADIC);
                this.is3dPartyFeatureActivated = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_3DPARTY_WEBRTC_PSTN_CALLING);
                this.isCCDAgentEnabled = this.ccdAgentService.isCcdAgentLogged();
                this.eventUnregisters.push(this.eventService.subscribe('ON_CALL_NOMADIC_EVENT', this.handleNomadicState));
                if (!this.isSipWise) {
                    this.eventUnregisters.push(this.eventService.subscribe('ON_CONTACT_PRESENCE_EVENT', () => {
                        this.handleNomadicState(null, this.telephonyService.nomadicObject);
                    }));

                    this.handleNomadicState(null, this.telephonyService.nomadicObject);
                }
                else {
                    this.eventUnregisters.push(this.eventService.subscribe('ON_SIPWISE_ROUTING_EVENT', (_event, currentDeviceType, sipWiseObject) => {

                        this.handleCallSipWiseState(_event, currentDeviceType, sipWiseObject); }));
                    this.telephonyService.getSipWiseRoutingInfo();
                    this.handleNomadicState(null, this.telephonyService.nomadicObject);//RQRAINB-2660
                }

                this.subscriptions.push(this.contact.subscribe((event) => {
                    try {
                        if (!event) return;
                        this.handleNomadicState(null, this.telephonyService.nomadicObject);
                    }
                    catch {}
                }));


                // Handle active call
                // The activeCall property is computed from telephony presence (and not from telephony service which contains only calls on current device)
                // to ensure that we take into account both calls on office phone and calls on nomadic device:
                // we want to disable the change of nomadic mode if a call exists on either the office or the nomadic phone
                this.eventUnregisters.push(this.eventService.subscribe('ON_UPDATE_MYTELEPHONY_STATUS_EVENT', () => { this.computeMode(); }));
                this.eventUnregisters.push(this.eventService.subscribe('ON_UPDATE_TELEPHONY_ICON_EVENT', () => { this.computeMode(); }));
            }

            this.subscriptions.push(this.userService.subscribe((event: any) => {
                if (event.name === UserServiceEvents.ON_USER_CHANGED) {
                    if (this.contactService.isUserContact(event.data.user)) {
                        // reinitialize service data after user telephony config changed (by admin...)
                        if (this.telConfigChanged(telConfig)) {
                            this.logger.info("[TelephonyConfigService] User config changed => reinitialize service");
                            this.computeTelephonySituation();
                        }
                    }
                }
            }));
        }
        catch {
            this.logger.error(`[TelephonyConfigService] computeTelephonySituation -- failure --`);
        }
    } //end computeTelephonySituation

    private telConfigChanged(telConfig: any): boolean {
        return this.contactService.userContact?.pbxId !== telConfig.pbxId ||
            this.contactService.userContact?.phoneInternalNumber !== telConfig.phoneInternalNumber ||
            this.contactService.userContact?.hasPhoneProDdiNumber !== telConfig.hasPhoneProDdiNumber ||
            this.contactService.userContact?.telephonySettingsEnabled !== telConfig.telephonySettingsEnabled ||
            this.contactService.userContact?.isVirtualTerm !== telConfig.isVirtualTerm ||
            this.contactService.userContact?.useComputerMode !== telConfig.useComputerMode ||
            this.contactService.userContact?.useOtherPhoneMode !== telConfig.useOtherPhoneMode ||
            this.contactService.userContact?.phoneProCan !== telConfig.phoneProCan ||
            this.contactService.userContact?.mobileProCan !== telConfig.mobileProCan ||
            this.contactService.userContact?.phonePersoCan !== telConfig.phonePersoCan ||
            this.contactService.userContact?.mobilePersoCan !== telConfig.mobilePersoCan ||
            this.contactService.userContact?.mobilePro !== telConfig.mobilePro ||
            this.contactService.userContact?.mobilePerso !== telConfig.mobilePerso ||
            this.contactService.userContact?.phonePerso !== telConfig.phonePerso ||
            this.contactService.userContact?.ccdAgent?.mode !== telConfig.ccdAgent?.mode ||
            this.contactService.userContact?.ccdAgent?.agentPhoneNumber !== telConfig.ccdAgent?.agentPhoneNumber;
    }

    private computeMode(): void {
        this.evalChangeModePossible();

        // Telephony is started ( no nomadic)
        delete this.routingMode.currentDestination;
        delete this.routingMode.routingError;
        if (this.routingMode.isTelephonyStarted && !this.isNomadicFeatureAllowed && !this.isNomadicFeatureActivated && !this.isSipWise) {
            this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.OFFICE_PHONE;
        }
        else {
            if (this.isNomadicFeatureActivated && !this.isSipWise) {

                if (this.isMakeCallInitiatorIsMain && !this.contact.isVirtualTerm) {
                    this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.OFFICE_PHONE;
                }
                else if (this.contact.isVirtualTerm && this.nomadicToNumber === "") {
                    this.routingMode.routingError = TelephonyRoutingErrors.NO_SECONDARY_PHONE_ASSOCIATED;
                }
                else if (this.contact.isVirtualTerm && this.isMakeCallInitiatorIsMain && !this.isNomadicModeActivated) {
                    this.routingMode.routingError = TelephonyRoutingErrors.MOBILE_PHONE_IS_IN_LOGOUT;
                }
                else if (this.nomadicPhone === "mediaPillar") {
                    if (this.isMediaPillarAvailable && this.contact.useComputerMode) {
                        this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.COMPUTER;
                    }
                    else if (this.isMediaPillarAvailable && !this.contact.useComputerMode) {
                        this.routingMode.routingError = TelephonyRoutingErrors.NO_SECONDARY_PHONE_ASSOCIATED;
                    }
                    else {
                        this.routingMode.routingError = TelephonyRoutingErrors.MEDIAPILLAR_UNAVAILABLE;
                    }
                }
                else if (this.nomadicPhone === "mobilePro") {
                    this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.MOBILE_PRO;
                }
                else if (this.nomadicPhone === "phonePerso") {
                    this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.PHONE_PERSO;
                }
                else if (this.nomadicPhone === "mobilePerso") {
                    this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.MOBILE_PERSO;
                }
                else {
                    this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.OTHER_PHONE;
                    this.routingMode.currentDestination = this.nomadicToNumber;
                }
            }
            else if (this.isSipWise) {
                if (this.webRTCDevice && this.webRTCDevice.main) {
                    if (this.isMediaPillarAvailable) {
                        this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.COMPUTER;
                    }
                    else {
                        this.routingMode.routingError = TelephonyRoutingErrors.MEDIAPILLAR_UNAVAILABLE;
                    }
                }
                else if (this.sipDevice && this.sipDevice.main) {
                    if (this.sipDevice.state === "inService") { //CRRAINB-31749
                        this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.OFFICE_PHONE;
                    } else {
                        this.routingMode.routingError = TelephonyRoutingErrors.OFFICE_PHONE_UNAVAILABLE;
                    }
                }
            }
            else if (!this.isNomadicFeatureActivated && this.contact.isVirtualTerm) {
                this.routingMode.routingError = TelephonyRoutingErrors.WRONG_CONFIG;
            }
            // No foward or Nomadic
            else {
                this.routingMode.currentDeviceType = TelephonyRoutingDeviceTypes.OFFICE_PHONE;
            }

            this.routingMode.isOnlyOfficePhoneRinging = !this.isNomadicModeActivated;
        }
        this.broadcastRoutingMode();
    }

    private broadcastRoutingMode() {
        if (JSON.stringify(this.oldRoutingMode) !== JSON.stringify(this.routingMode)) { //trig the observable only if some change
            this.logger.debug('[TelephonyConfigService] pullTelephonyMode : evt trigged : ' + JSON.stringify(this.routingMode));
            this.sendEvent(TelephonyConfigServiceEvents.ON_ROUTING_MODE_CHANGE, this.routingMode);
            this.oldRoutingMode = JSON.parse(JSON.stringify(this.routingMode));
        }
    }

    private broadcastRoutingDevicesList(newRoutingDeviceList: TelephonyRoutingDevice[]) {
        if (JSON.stringify(newRoutingDeviceList) !== JSON.stringify(this.routingDeviceList)) {
            this.logger.debug('[TelephonyConfigService] pullRoutingDevicesList : evt trigged : ' + JSON.stringify(newRoutingDeviceList));
            this.sendEvent(TelephonyConfigServiceEvents.ON_ROUTING_LIST_CHANGE, newRoutingDeviceList);
            this.routingDeviceList = JSON.parse(JSON.stringify(newRoutingDeviceList));
        }
    }

    private mediaPillarPollingManagement(selectedMode) {
        if (this.isMediaPillarConfigured && selectedMode !== TelephonyRoutingDeviceTypes.COMPUTER) {
            this.webrtcGatewayService.mediaPillarUserSelectAndPolling(false);
        }
    }
}
